import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Camera, Loader2, ArrowLeft, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';
import { SEO } from '@/components/SEO';
import { StyleChips } from '@/components/marketplace/DirectoryFilters';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage, getMarketplaceStyles } from '@/lib/api';
import { isPasswordStrongEnough } from '@/lib/passwordStrength';
import { SIGNUP_PHOTOGRAPHER_PAGE, SIGNUP_PHOTOGRAPHER_MARKETPLACE as MP } from '@/constants/testIds';

const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';

/**
 * Photographer onboarding. Two doors, one form:
 *  - with an invite code: LXRA teammate (team invite) or pre-approved Marketplace studio
 *  - without a code ("apply"): own Marketplace studio, pending LXRA approval
 */
export default function SignupPhotographer() {
  const { signupAsPhotographer } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applyMode = searchParams.get('apply') === '1';
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');
  const [name, setName] = useState('');
  const [studioName, setStudioName] = useState('');
  const [homeBase, setHomeBase] = useState('');
  const [styles, setStyles] = useState([]);
  const [allStyles, setAllStyles] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingStudio, setPendingStudio] = useState(null);

  useEffect(() => { getMarketplaceStyles().then(setAllStyles).catch(() => setAllStyles([])); }, []);

  const isApplying = !inviteCode.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isPasswordStrongEnough(password)) {
      setError('Please meet all the password requirements below.');
      return;
    }
    setLoading(true);
    try {
      const data = await signupAsPhotographer({
        inviteCode: inviteCode.trim() || null, email: email.trim(), password, name: name.trim(),
        studioName: studioName.trim(), homeBase: homeBase.trim(), styles,
      });
      if (data.studio?.status === 'pending') setPendingStudio(data.studio);
      else navigate('/admin');
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create your account. Please check your details.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#0A0A0A] px-4 py-16" data-testid={SIGNUP_PHOTOGRAPHER_PAGE.root}>
      <SEO title={applyMode ? 'Apply to Join' : 'Join the Studio'} description="Join the LXRA network — by invite or by application." />
      <div className="w-full max-w-xl">
        <Link to={applyMode ? '/photographers' : '/login'} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" />
          {applyMode ? 'Back to photographers' : 'Back to sign in'}
        </Link>

        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 sm:p-10">
          {pendingStudio ? (
            <div data-testid={MP.pendingBanner}>
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#C9A45C]/45 bg-[#C9A45C]/10 text-[#D7B46A] mb-5"><Clock className="h-5 w-5" /></span>
              <p className="text-xs uppercase tracking-[0.18em] text-[#D7B46A]">Pending LXRA approval</p>
              <h1 className="font-display text-3xl tracking-[0.06em] text-[hsl(var(--lxra-fg))] mt-2">APPLICATION RECEIVED.</h1>
              <p className="mt-3 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
                Thanks, {name || 'there'}. <span className="text-[hsl(var(--lxra-fg))]">{pendingStudio.name}</span> is reserved at <span className="lxra-mono text-[hsl(var(--lxra-fg))]">/p/{pendingStudio.handle}</span>.
                We review applications within 2–3 business days. You can set up your profile, packages and galleries now — your public page goes live once approved.
              </p>
              <Button onClick={() => navigate('/admin')} data-testid="signup-photographer-go-dashboard" className="mt-7 h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold">
                Set up my profile
              </Button>
            </div>
          ) : (
            <>
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] mb-5">
                <Camera className="h-5 w-5" />
              </span>
              <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Marketplace</p>
              <h1 className="font-display text-3xl tracking-[0.06em] text-[hsl(var(--lxra-fg))] mt-2">{applyMode ? 'APPLY TO JOIN.' : 'JOIN THE NETWORK.'}</h1>
              <p data-testid={MP.modeHint} className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
                {isApplying
                  ? 'No invite? Apply below. Your studio lands in review and goes public once LXRA approves it.'
                  : 'Your invite code is filled in — finish creating your account to get started right away.'}
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="invite-code" className={labelClass}>Invite code (optional)</Label>
                  <Input id="invite-code" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="Paste an invite code if you have one" data-testid={SIGNUP_PHOTOGRAPHER_PAGE.inviteCodeInput} className={fieldClass} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="pg-name" className={labelClass}>Your name</Label>
                    <Input id="pg-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid={SIGNUP_PHOTOGRAPHER_PAGE.nameInput} className={fieldClass} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pg-studio" className={labelClass}>Studio / brand name</Label>
                    <Input id="pg-studio" value={studioName} onChange={(e) => setStudioName(e.target.value)} placeholder={name ? `${name} Photography` : 'e.g. North Light Studio'} data-testid={MP.studioNameInput} className={fieldClass} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pg-home" className={labelClass}>Home base</Label>
                  <Input id="pg-home" value={homeBase} onChange={(e) => setHomeBase(e.target.value)} placeholder="City or neighborhood, e.g. Silver Spring, MD" data-testid={MP.homeBaseInput} className={fieldClass} />
                </div>
                {allStyles.length > 0 && (
                  <div className="space-y-2">
                    <Label className={labelClass}>Your style (pick up to 4)</Label>
                    <StyleChips
                      styles={allStyles}
                      selected={styles}
                      onToggle={(s) => setStyles((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : cur.length < 4 ? [...cur, s] : cur))}
                      testId={MP.styleChips}
                      chipTestId={MP.styleChip}
                      size="sm"
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="pg-email" className={labelClass}>Email</Label>
                    <Input id="pg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid={SIGNUP_PHOTOGRAPHER_PAGE.emailInput} className={fieldClass} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="pg-password" className={labelClass}>Password</Label>
                    <Input id="pg-password" type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} data-testid={SIGNUP_PHOTOGRAPHER_PAGE.passwordInput} className={fieldClass} />
                    <PasswordStrengthMeter password={password} testIdPrefix={SIGNUP_PHOTOGRAPHER_PAGE.passwordInput} />
                  </div>
                </div>

                {error && <p className="text-xs text-red-400" data-testid={SIGNUP_PHOTOGRAPHER_PAGE.errorText}>{error}</p>}

                <Button type="submit" disabled={loading} data-testid={SIGNUP_PHOTOGRAPHER_PAGE.submitButton} className="w-full h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isApplying ? 'Submit application' : 'Create account'}
                </Button>
              </form>

              <p className="mt-6 text-center text-xs text-[hsl(var(--lxra-muted))]">
                Already on the network?{' '}
                <Link to="/login" data-testid={SIGNUP_PHOTOGRAPHER_PAGE.loginLink} className="text-[hsl(var(--lxra-gold))] hover:underline">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
